import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import CreatePricingPlanFormModal from 'src/features/pricingPlan/components/form/CreatePricingPlanFormModal';
import CellPricingPlanActions from 'src/features/pricingPlan/components/table/CellPricingPlanActions';
import VersionStatusBadge from 'src/features/pricingPlan/components/VersionStatusBadge';
import {
    pricingBillingBadgeOptions,
    pricingCalculationBadgeOptions,
    pricingStatusBadgeOptions,
} from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { CellBadge, CellDatetime } from 'src/registry/components/table';
import { CellLink } from 'src/registry/components/table/cells/CellLink';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages } from 'src/utils/messages';

const sortingOptions = {
    initialSortingState: [
        { id: 'valid_from', desc: true },
        { id: 'name', desc: true },
    ],
};
const filterOptions = { initialQueryFilters: { by_latest_version: 'true' } };
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<PricingPlan>();

function PricingPlans() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { queryFiltersOptions, ...filters } = useFilters(filterOptions);
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions, parkingId],
    );

    const { data, isLoading, isError } = useListPricingPlans(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(pricingPlanMessages.pricingPlans));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: (ctx) => {
                const name = ctx.row.original.translations.find((translation) => translation.language === locale)?.name;
                return (
                    <div className="flex flex-col items-start">
                        <CellLink
                            to={`/${PATH.ADMINISTRATION}/pricing-plan/${ctx.row.original.id}`}
                            render={() => name}
                            {...ctx}
                        />
                        <VersionStatusBadge
                            isCurrentVersion={ctx.row.original.is_current_version}
                            validFrom={ctx.row.original.valid_from}
                        />
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('billing_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                badgeOptions: pricingBillingBadgeOptions,
                title: formatMessage(pricingPlanMessages.billingType),
            },
        }),
        columnHelper.accessor('calculation_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                badgeOptions: pricingCalculationBadgeOptions,
                title: formatMessage(pricingPlanMessages.calculationType),
            },
        }),
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(pricingPlanMessages.versionNumber),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeTo),
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                badgeOptions: pricingStatusBadgeOptions,
                title: formatMessage(pricingPlanMessages.status),
            },
        }),
        columnHelper.accessor('is_system', {
            header: DataTableColumnHeader,
            enableSorting: false,
            enableHiding: true,
            cell: ({ row }) => <BooleanBadge booleanField={row.original.is_system} />,
            meta: {
                title: formatMessage(pricingPlanMessages.isSystem),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellPricingPlanActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<PricingPlan, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(pricingPlanMessages.pricingPlans),
                entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.pricingPlans).toLowerCase(),
                placeholder: formatMessage(pricingPlanMessages.pricingPlan),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(managePricingPlans && { newEntry: CreatePricingPlanFormModal }),
            }}
        />
    );
}

export default PricingPlans;
